
import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import TocTocHeader from "@/components/TocTocHeader";
import TocTocFooter from "@/components/TocTocFooter";
import { Calendar, User, ArrowLeft, Share2, Tag } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { BlogPost } from "@/types/blog";
import { blogService } from "@/services/blogService";
import { toast } from "sonner";
import { isBrowser } from "@/utils/runtime";
import SEOHead from "@/components/common/SEOHead";
import { getBlogCoverImage } from "@/utils/blogImages";

const BlogPost = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const formatDate = (value?: string | null) => {
    if (!value) return "";
    try {
      return new Date(value).toLocaleDateString("pt-BR");
    } catch (error) {
      return value;
    }
  };

  useEffect(() => {
    const loadPost = async () => {
      if (!isBrowser) return;
      if (!slug) {
        navigate("/blog");
        return;
      }

      try {
        const foundPost = await blogService.getBySlug(slug);

        if (foundPost && foundPost.status === "published") {
          setPost(foundPost);
        } else {
          navigate("/blog");
        }
      } catch (error) {
        console.error("Error loading post:", error);
        toast.error("Erro ao carregar o post");
        navigate("/blog");
      } finally {
        setIsLoading(false);
      }
    };

    loadPost();
  }, [slug, navigate]);

  const handleShare = async () => {
    if (!isBrowser || !post) return;
    if (navigator.share) {
      try {
        await navigator.share({
          title: post.title,
          text: post.excerpt ?? post.seo_description ?? "",
          url: globalThis.location.href,
        });
      } catch (error) {
        console.log("Error sharing:", error);
      }
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(globalThis.location.href);
      toast.success("Link copiado para a área de transferência!");
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col">
        <TocTocHeader />
        <main className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#FF5605]"></div>
        </main>
        <TocTocFooter />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen flex flex-col">
        <TocTocHeader />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-[#323031] mb-4">Post não encontrado</h1>
            <Button onClick={() => navigate('/blog')} className="bg-[#FF5605] hover:bg-[#E14D04]">
              Voltar ao Blog
            </Button>
          </div>
        </main>
        <TocTocFooter />
      </div>
    );
  }

  const coverImage = getBlogCoverImage(post);
  const authorName = post.author ?? "Equipe Toc Toc";
  const content = post.content ?? "";
  const tags = post.tags ?? post.slug.split("-").filter(Boolean);
  const description = post.seo_description ?? post.excerpt ?? "";

  const schema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": post.title,
    "description": description,
    "image": coverImage,
    "datePublished": post.published_at ?? post.created_at,
    "dateModified": post.updated_at ?? post.published_at ?? post.created_at,
    "author": {
      "@type": "Person",
      "name": authorName,
    },
    "publisher": {
      "@type": "Organization",
      "name": "Toc Toc Fotografia Imobiliária",
      "logo": {
        "@type": "ImageObject",
        "url": "https://site.toctocbr.com/lovable-uploads/24e0e29e-3e10-43db-8be9-1cc4bc18ab2f.png"
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://site.toctocbr.com/blog/${post.slug}`,
    },
    "keywords": tags.join(", "),
    "articleSection": "Fotografia Imobiliária",
    "inLanguage": "pt-BR"
  };

  return (
    <>
      <SEOHead
        title={post.seo_title || post.title}
        description={description}
        keywords={`${tags.join(", ")}, fotografia imobiliária, marketing imobiliário, Balneário Camboriú, Itajaí, Itapema, Santa Catarina`}
        canonicalUrl={`/blog/${post.slug}`}
        schema={schema}
        ogImage={coverImage}
        ogImageAlt={post.title}
        titleTemplate={null}
      />
      
      <div className="min-h-screen flex flex-col">
        <TocTocHeader />
        <main className="flex-1">
        {/* Back button */}
        <div className="container max-w-4xl mx-auto px-4 py-6">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/blog')}
            className="mb-6 text-[#5B5A59] hover:text-[#FF5605]"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Voltar ao Blog
          </Button>
        </div>

        {/* Hero section */}
        <section className="py-8 bg-gray-50">
          <div className="container max-w-4xl mx-auto px-4">
            <div className="mb-6">
              <span className="inline-block px-3 py-1 bg-[#FF5605] text-white text-sm font-medium rounded-full mb-4">
                Conteúdo Toc Toc
              </span>
              <h1 className="text-3xl md:text-4xl font-bold text-[#323031] mb-4">
                {post.title}
              </h1>
              <p className="text-lg text-[#5B5A59] mb-6">
                {post.excerpt}
              </p>
              
              {/* Meta information */}
              <div className="flex flex-wrap items-center gap-6 text-[#5B5A59] mb-6">
                <div className="flex items-center space-x-2">
                  <User className="h-5 w-5 text-[#FF5605]" />
                  <span>{authorName}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Calendar className="h-5 w-5 text-[#FF5605]" />
                  <span>{formatDate(post.published_at)}</span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleShare}
                  className="text-[#5B5A59] hover:text-[#FF5605]"
                >
                  <Share2 className="h-4 w-4 mr-2" />
                  Compartilhar
                </Button>
              </div>
              
              {/* Tags */}
              <div className="flex flex-wrap gap-2">
                {tags.map((tag, index) => (
                  <span
                    key={`${tag}-${index}`}
                    className="inline-flex items-center px-3 py-1 bg-white text-[#5B5A59] text-sm rounded-full"
                  >
                    <Tag className="h-3 w-3 mr-1" />
                    {tag}
                  </span>
                ))}
              </div>
            </div>
            
            {/* Featured image */}
            <div className="rounded-2xl overflow-hidden shadow-lg mb-8">
              <img
                src={coverImage}
                alt={post.title}
                className="w-full h-64 md:h-96 object-cover"
              />
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="py-12">
          <div className="container max-w-4xl mx-auto px-4">
            <article className="prose prose-lg max-w-none prose-headings:text-[#323031] prose-p:text-[#5B5A59] prose-a:text-[#FF5605] prose-strong:text-[#323031] whitespace-pre-wrap">
              {content}
              
              {/* Internal Links Section */}
              <div className="mt-12 p-6 bg-[#F6E5C9]/20 rounded-xl border-l-4 border-[#FF5605]">
                <h3 className="text-xl font-bold text-[#323031] mb-4">Saiba mais sobre nossos serviços</h3>
                <ul className="space-y-2">
                  <li>
                    <Link to="/servicos" className="text-[#FF5605] hover:underline font-medium">
                      Conheça nossos serviços de fotografia imobiliária em Santa Catarina
                    </Link>
                  </li>
                  <li>
                    <Link to="/portfolio" className="text-[#FF5605] hover:underline font-medium">
                      Veja +30 mil fotos no nosso portfólio de fotografia imobiliária
                    </Link>
                  </li>
                  <li>
                    <Link to="/cidades-atendidas" className="text-[#FF5605] hover:underline font-medium">
                      Cidades atendidas: Balneário Camboriú, Itajaí, Itapema e região
                    </Link>
                  </li>
                </ul>
              </div>
            </article>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-[#323031] text-white">
          <div className="container max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-2xl md:text-3xl font-bold mb-6">
              Gostou do conteúdo? <span className="text-[#FF5605]">Vamos conversar!</span>
            </h2>
            <p className="text-lg text-white/90 mb-8">
              Entre em contato conosco para descobrir como podemos ajudar você a vender mais através da fotografia profissional.
            </p>
            <Button
              className="bg-[#FF5605] hover:bg-[#E14D04] text-white px-8 py-3 rounded-xl text-lg"
              onClick={() => isBrowser && globalThis.open('https://wa.me/554797367396?text=Olá! Vi o blog da Toc Toc e gostaria de saber mais sobre os serviços de fotografia imobiliária.', '_blank')}
            >
              Falar com Especialista
            </Button>
          </div>
        </section>
      </main>
      <TocTocFooter />
    </div>
    </>
  );
};

export default BlogPost;
